import React, { useState } from "react";
import { Mail, Phone, MapPin, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Reveal, SectionHeading } from "../Reveal";
import { contactInfo } from "../../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", organization: "", email: "", phone: "", message: "" };

const ContactSection = () => {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/leads`, form);
      setForm(initial);
      toast.success("Thank you! Our healthcare team will reach out within 24 hours.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send your request. Please try again or call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 lg:py-32">
      <div className="bg-radial-glow absolute inset-0 opacity-60" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeading
          eyebrow="Book a Demo"
          title={<>Let's transform your <span className="text-cyan-400">patient experience</span></>}
          sub="Tell us about your hospital and we'll arrange a live walkthrough of the Arkision voice agent — with a free test account."
        />

        <div className="grid gap-6 lg:grid-cols-5">
          <Reveal className="lg:col-span-3">
            <form onSubmit={submit} className="card-dark rounded-3xl p-7 sm:p-9">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Full Name *</label>
                  <Input value={form.name} onChange={set("name")} placeholder="Dr. Sarah Ahmed" className="border-white/10 bg-white/5 text-white placeholder:text-slate-600 focus-visible:ring-cyan-400/50" />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Hospital / Clinic</label>
                  <Input value={form.organization} onChange={set("organization")} placeholder="City National Hospital" className="border-white/10 bg-white/5 text-white placeholder:text-slate-600 focus-visible:ring-cyan-400/50" />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Work Email *</label>
                  <Input type="email" value={form.email} onChange={set("email")} placeholder="you@hospital.com" className="border-white/10 bg-white/5 text-white placeholder:text-slate-600 focus-visible:ring-cyan-400/50" />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Phone</label>
                  <Input value={form.phone} onChange={set("phone")} placeholder="+966 5x xxx xxxx" className="border-white/10 bg-white/5 text-white placeholder:text-slate-600 focus-visible:ring-cyan-400/50" />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Message *</label>
                  <Textarea value={form.message} onChange={set("message")} rows={4} placeholder="Tell us about your call volumes, languages needed and current systems (HIS/EMR)..." className="border-white/10 bg-white/5 text-white placeholder:text-slate-600 focus-visible:ring-cyan-400/50" />
                </div>
              </div>
              <Button type="submit" disabled={sending} size="lg" className="mt-7 w-full rounded-full bg-cyan-400 font-semibold text-slate-950 transition-colors hover:bg-cyan-300 sm:w-auto sm:px-10">
                {sending ? "Sending..." : <><Send className="mr-2 h-4 w-4" /> Request Demo</>}
              </Button>
              <p className="mt-4 flex items-center gap-2 text-xs text-slate-500">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> Response within 24 hours · Your data stays with you
              </p>
            </form>
          </Reveal>

          <Reveal delay={150} className="lg:col-span-2">
            <div className="card-dark flex h-full flex-col rounded-3xl p-7 sm:p-9">
              <h3 className="font-display text-lg font-semibold text-white">{contactInfo.gccPartner.name}</h3>
              <p className="mt-1 text-sm text-cyan-300/80">{contactInfo.gccPartner.partner} · {contactInfo.gccPartner.role}</p>
              <div className="mt-6 space-y-5 text-sm">
                <div className="flex items-start gap-3">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" />
                  <span className="leading-relaxed text-slate-400">{contactInfo.gccPartner.address}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 shrink-0 text-cyan-400" />
                  <a href={`mailto:${contactInfo.gccPartner.email}`} className="text-slate-300 transition-colors hover:text-cyan-300">{contactInfo.gccPartner.email}</a>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 shrink-0 text-cyan-400" />
                  <a href={`tel:${contactInfo.gccPartner.phone.replace(/\s/g, "")}`} className="text-slate-300 transition-colors hover:text-cyan-300">{contactInfo.gccPartner.phone}</a>
                </div>
              </div>

              <div className="mt-7 border-t border-white/10 pt-6">
                <h3 className="font-display text-lg font-semibold text-white">{contactInfo.indiaOffice.name}</h3>
                <div className="mt-4 space-y-4 text-sm">
                  <div className="flex items-start gap-3">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" />
                    <span className="leading-relaxed text-slate-400">{contactInfo.indiaOffice.address}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 shrink-0 text-cyan-400" />
                    <a href={`mailto:${contactInfo.indiaOffice.email}`} className="text-slate-300 transition-colors hover:text-cyan-300">{contactInfo.indiaOffice.email}</a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="h-4 w-4 shrink-0 text-cyan-400" />
                    <a href={`tel:${contactInfo.indiaOffice.phone.replace(/\s/g, "")}`} className="text-slate-300 transition-colors hover:text-cyan-300">{contactInfo.indiaOffice.phone}</a>
                  </div>
                </div>
              </div>
              <div className="mt-auto pt-8">
                <div className="rounded-2xl border border-white/10 bg-black/30 p-5">
                  <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">What happens next</span>
                  <ul className="mt-3 space-y-2 text-sm text-slate-400">
                    <li>· We understand your patient call use-cases</li>
                    <li>· Live demo tailored to your hospital</li>
                    <li>· Free test account to try Arkision</li>
                    <li>· Setup best practices & pricing</li>
                  </ul>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
